import React from "react";
import { _goToFactor } from "routers/helper";

import { View, StyleSheet, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Icon from "components/Icon";
import Button from "components/Button";
import LoadingBox from "components/boxes/LoadingBox";
import ConfirmAlert from "components/ConfirmAlert";
import ModalOPtions from "components/modals/ModalOPtions";
import DeleteAlertComponent from "components/DeleteAlertComponent";

import { strings, colors, sizes } from "assets/constants";
import { useFlag } from "hooks";

import { useDeleteFactor } from "clients/apis";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import css from "styles";
import Shadow from "./Shadow";
import tinycolor from "tinycolor2";

export default function FactorBox(props: {
  factor: FactorT;
  activeStep?: any;
  actions?: Array<"delete" | "changeStatus">;
  refreshList?: () => void;
  statuses: { [key: number]: FactorStatusT };
}) {
  const {
    factor: {
      id,
      created_at: date,
      customer,
      counts,
      is_from_wholesaler,
      read,
      factor_status_org,
    },
    activeStep,
    refreshList,
    statuses,
    actions,
  } = props;

  const {
    company: { color },
  } = useRecoilValue(authAtom);

  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();
  const { flg: options, on: showOptions, off: hideOptions } = useFlag();
  const _onShowOptions = () => {
    showOptions();
  };

  const deleteFactorMut = useDeleteFactor();

  const _onDelete = () => {
    hideOptions();
    showAlert();
  };
  const _onDeleteConfirm = (close: () => void) => {
    close();
    deleteFactorMut.mutate(id, { onSuccess: refreshList && refreshList });
  };

  const _goToEdit = () =>
    _goToFactor({ data: props.factor, steps: statuses, activeStep });

  return (
    <>
      <TouchableOpacity
        onPress={_goToEdit}
        onLongPress={_onShowOptions}
        activeOpacity={sizes.tapOpacity}
        style={{
          ...styles.container,
          borderColor: options ? color : colors.transparent,
        }}
      >
        <View style={styles.tag}>
          <Text
            weight="extraBold"
            size={"xs"}
            color={
              read === 0
                ? colors.warning
                : factor_status_org === "ACCEPTED"
                ? colors.success
                : factor_status_org === "REJECTED"
                ? colors.danger
                : colors.cyan
            }
          >
            {read === 0
              ? strings.new
              : factor_status_org === "ACCEPTED"
              ? strings.accepted
              : factor_status_org === "REJECTED"
              ? strings.rejected
              : strings.draft_awaiting_review}
          </Text>
          <Text color={colors.text.dark} weight="bold" size={"xs"}>
            {" "}
            {strings.status}:{" "}
          </Text>
        </View>

        <View style={styles.header}>
          <View style={css.rRowCenterAlign}>
            <Icon name="calendar" size={13} color={color} />
            <Text
              style={{ marginRight: sizes.iconTextSpace, marginLeft: 10 }}
              weight="bold"
              size={"sm"}
              color={colors.text.gray}
            >
              {date?.split(" ")?.[0]}
            </Text>

            <Icon name="clock" size={13} color={color} />
            <Text
              style={{ marginRight: sizes.iconTextSpace }}
              weight="bold"
              size={"sm"}
              color={colors.text.gray}
            >
              {date?.split(" ")?.[1]}
            </Text>
          </View>
          <View style={{ width: 15 }} />
          <View
            style={{ ...styles.growContainer, marginRight: sizes.space / 2 }}
          >
            <View style={{ flexDirection: "row-reverse", marginBottom: 5 }}>
              <Text color={colors.text.gray} weight="medium">
                فاکتور شماره{" "}
              </Text>
              <Text weight="extraBold" color={colors.text.dark}>
                {id}
              </Text>
            </View>
          </View>
        </View>

        <View
          style={{
            marginVertical: 3,
            paddingHorizontal: 5,
            flexDirection: "row-reverse",
            justifyContent: "space-between",
          }}
        >
          <View style={css.rRowCenterAlign}>
            <View style={{ marginLeft: 4 }}>
              <Icon
                size={12}
                name="user"
                color={tinycolor(colors.theme).lighten(25).toString()}
              />
            </View>
            <Text numberOfLines={1} weight="extraBold">
              {customer?.name}
            </Text>
          </View>

          <View style={css.cover} />

          {is_from_wholesaler === 1 && (
            <>
              <View style={{ marginHorizontal: 4 }}>
                <Text weight="bold" color={colors.text.gray}>
                  ثبت توسط:
                </Text>
              </View>

              <Text weight="extraBold" size={"sm"} color={colors.info}>
                {strings.wholesaler}
              </Text>
            </>
          )}
        </View>

        <View style={styles.footer}>
          <Icon style={{ left: -5 }} size={22} name="chevron-left" />

          <View style={styles.footerRow}>
            {counts.count > 0 && counts.total > 0 ? (
              <>
                <Text size={"xs"}> {counts.unit} )</Text>
                <Text weight="bold" size="lg" color={colors.textColorDark}>
                  {counts.total}
                </Text>
                <Text size={"xs"}>{strings.type_of_product} ( </Text>
                <Text weight="bold" size="lg" color={colors.textColorDark}>
                  {" "}
                  {counts.count}{" "}
                </Text>
                <Text size={"xs"}> {strings.count}</Text>
                <Icon color={color} size={17} name="figma" />
              </>
            ) : (
              <Text>{strings.empty_factor}</Text>
            )}
          </View>
        </View>
      </TouchableOpacity>

      <ModalOPtions
        flag={options}
        hide={hideOptions}
        renderActions={() => {
          return (
            <>
              {actions?.includes("delete") && (
                <Button
                  onPress={_onDelete}
                  width={45}
                  height={45}
                  bgColor={colors.transparent}
                  style={{ marginHorizontal: 8 }}
                  icon={{ name: "trash", size: 25, color: colors.danger }}
                />
              )}
            </>
          );
        }}
      />

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            remove={() => _onDeleteConfirm(close)}
            cancel={close}
          />
        )}
      />

      {deleteFactorMut?.isLoading && <LoadingBox />}
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    borderRadius: sizes.radius.xl,
    backgroundColor: colors.itemsBg,
    paddingHorizontal: sizes.space,
    paddingBottom: sizes.space,
    borderWidth: 1,
  },

  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    height: 30,
  },

  footer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    height: 30,
  },

  growContainer: { flexGrow: 1 },

  footerRow: { flexDirection: "row", alignItems: "center" },

  tag: {
    height: 25,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: colors.white,
  },
});
